// QBR rep-authored section-input API (Section 1 free text).
//
// Backend contract (src/api_layer/routers/kpi_rep.py):
//   PATCH /kpi_metrics/qbr_report/rep_inputs[?quarter=Q<n><yyyy>]
//            { field_name, field_value } -> 204 No Content
//          field_name ∈ { payor_reimbursement_dynamics, measures_of_success }
//          (RepInputPatchRequest). Omit ?quarter= to write under the current
//          quarter; a compact id (e.g. "Q22026") targets that prior quarter.
//          field_value "" / null clears the field.
//
// Saved values are read back through the bulk GET /kpi_metrics/qbr_report
// payload (qbr_report.rep_inputs and prior_quarters[*].qbr_report.rep_inputs),
// not a per-field GET — so getSectionInput below is only the legacy
// localStorage reader kept for non-section-1 draft fields.
//
// Auth is the HttpOnly session cookie (withCredentials) plus the Bearer/user
// headers in dev, identical to the KPI slices. The extra `client: "UT"` header
// mirrors kpiRequestHeaders in redux/slice/kpi-widget-metrics.js, which every
// /kpi_metrics call sends.
import axios from "axios";

import { getBaseUrl } from "../redux/config";
import { getAuthApiHeaders } from "../utils/common";

const baseUrl = getBaseUrl();

const REP_INPUTS_PATH = "/kpi_metrics/qbr_report/rep_inputs";

/** Section 1 id used by TerritoryOverviewCard. */
const SECTION1_ID = "section1";

/**
 * Field keys inside Section 1 that are persisted via the real backend API.
 * Values match the server-side ``field_name`` enum on ``RepInputPatchRequest``
 * so no translation layer is needed.
 */
const SECTION1_API_FIELDS = new Set([
  "payor_reimbursement_dynamics",
  "measures_of_success",
]);

/**
 * Compact quarter id the PATCH route accepts, e.g. ``Q22026``. Mirrors the
 * server-side ``pattern=r"^Q[1-4]\d{4}$"`` guard so a malformed id never
 * leaves the browser.
 */
const QUARTER_ID_PATTERN = /^Q[1-4]\d{4}$/;

const storageKey = (sectionId, fieldKey) => `qbr:${sectionId}:${fieldKey}`;

/** Shared request config: session cookie + standard auth + KPI `client` header. */
const authConfig = (extra = {}) => ({
  headers: {
    "Content-Type": "application/json",
    "use-case": "src",
    ...getAuthApiHeaders(),
    client: "UT",
  },
  withCredentials: true,
  ...extra,
});

/** True when (sectionId, fieldKey) targets the real backend API. */
function isApiBackedField(sectionId, fieldKey) {
  return sectionId === SECTION1_ID && SECTION1_API_FIELDS.has(fieldKey);
}

/**
 * Best-effort removal of the legacy localStorage entry after a successful API
 * save, so a stale cached value can't resurface via the getSectionInput
 * fallback.
 */
function removeLocalCopy(sectionId, fieldKey) {
  try {
    localStorage.removeItem(storageKey(sectionId, fieldKey));
  } catch {
    /* localStorage disabled / private mode — nothing to clean up. */
  }
}

/**
 * Persist the value for a single section input.
 *
 * Resolves ``{ok: true}`` on success or ``{ok: false, error}`` on failure —
 * never throws, so callers can keep their status logic to a plain truthy
 * check. Section 1 API-backed fields hit ``PATCH /kpi_metrics/qbr_report/
 * rep_inputs``; every other (sectionId, fieldKey) still writes localStorage so
 * surfaces not yet migrated keep working.
 *
 * @param {string} sectionId
 * @param {string} fieldKey
 * @param {string|null|undefined} value
 * @param {{quarterId?: string|null}} [options]
 *   ``quarterId`` is the compact ``Q<n><yyyy>`` id of the quarter to write
 *   under. ``null``/``undefined`` writes under the backend's current quarter.
 *   Malformed ids are dropped so a bad caller can't 422 the request.
 * @returns {Promise<{ok: boolean, error?: unknown}>}
 */
export async function saveSectionInput(
  sectionId,
  fieldKey,
  value,
  { quarterId } = {},
) {
  if (isApiBackedField(sectionId, fieldKey)) {
    try {
      // Only append ?quarter= when the caller supplied a well-formed id.
      const quarterParam =
        typeof quarterId === "string" && QUARTER_ID_PATTERN.test(quarterId)
          ? { params: { quarter: quarterId } }
          : {};
      await axios.patch(
        `${baseUrl}${REP_INPUTS_PATH}`,
        { field_name: fieldKey, field_value: value ?? "" },
        authConfig(quarterParam),
      );
      // Clear the legacy cache only after a confirmed server-side write so an
      // offline user keeps their draft locally until the API accepts it.
      removeLocalCopy(sectionId, fieldKey);
      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  }

  // Legacy localStorage path — for any non-section-1 draft field not yet
  // migrated to the backend.
  try {
    localStorage.setItem(storageKey(sectionId, fieldKey), value ?? "");
  } catch {
    // Swallow write errors so the UI never breaks on a failed persist.
  }
  return { ok: true };
}

/**
 * Load the saved value for a single section input.
 *
 * @deprecated for Section 1 fields — the parent seeds the initial value from
 * the Redux ``qbr_report.rep_inputs`` payload that ``fetchKpiQbrReport``
 * already fetches, avoiding a per-field round-trip on mount. Still exported
 * with the localStorage body for any other (future) draft field whose GET-side
 * isn't centralized yet. Resolves to ``""`` when nothing is stored.
 *
 * @param {string} sectionId
 * @param {string} fieldKey
 * @returns {Promise<string>}
 */
export function getSectionInput(sectionId, fieldKey) {
  return new Promise((resolve) => {
    try {
      resolve(localStorage.getItem(storageKey(sectionId, fieldKey)) ?? "");
    } catch {
      resolve("");
    }
  });
}
